import { useCallback, useRef, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { ResponseError } from '@backstage/errors';
import {
  codebaseAskApiRef,
  type CodebaseAnswer,
  type CodebaseScope,
} from './api';
import { buildPriorContext } from './priorContext';

/** What the drawer offers: unknown until the first open, then fixed until reload. */
export type AskAiScope =
  | { status: 'unknown' }
  | { status: 'loading' }
  | { status: 'member'; repos: CodebaseScope['repos'] }
  | { status: 'search' };

export type AskFailure =
  /** The gate refused; `message` is its user-facing reason. */
  | { kind: 'denied'; message: string }
  | { kind: 'rateLimited'; resetAt?: string }
  /** The Entra token could not be had or was refused. */
  | { kind: 'signIn' }
  /** The request body was over the route's size limit. */
  | { kind: 'tooLong' }
  | { kind: 'failed' }
  /** The person stopped waiting; the server may still be answering. */
  | { kind: 'stopped' };

export interface AskTurn {
  id: number;
  question: string;
  answer?: CodebaseAnswer;
  failure?: AskFailure;
}

export interface PendingAsk {
  question: string;
  startedAt: number;
}

export interface CodebaseConversation {
  scope: AskAiScope;
  /** Loads the scope on the first call; later calls do nothing. */
  loadScope(): void;
  turns: AskTurn[];
  pending?: PendingAsk;
  ask(question: string): void;
  /** Stops the browser waiting. The server is not told and the question still counts. */
  stopWaiting(): void;
  newConversation(): void;
}

function toFailure(error: unknown): AskFailure {
  if ((error as Error)?.name === 'AbortError') return { kind: 'stopped' };
  if (error instanceof ResponseError) {
    const body = error.body?.error as
      | { message?: string; resetAt?: unknown }
      | undefined;
    switch (error.statusCode) {
      case 401:
        return { kind: 'signIn' };
      case 403:
        return { kind: 'denied', message: body?.message ?? '' };
      case 429:
        return {
          kind: 'rateLimited',
          resetAt: typeof body?.resetAt === 'string' ? body.resetAt : undefined,
        };
      case 413:
        return { kind: 'tooLong' };
      default:
        return { kind: 'failed' };
    }
  }
  if ((error as Error)?.name === 'CodebaseAskUnavailableError') {
    return { kind: 'signIn' };
  }
  return { kind: 'failed' };
}

/**
 * The Ask AI conversation, held by AskAiLauncher (rendered once in Root) so
 * it survives closing the drawer and moving between pages, and is lost on
 * reload.
 */
export function useCodebaseConversation(): CodebaseConversation {
  const api = useApi(codebaseAskApiRef);
  const [scope, setScope] = useState<AskAiScope>({ status: 'unknown' });
  const [turns, setTurns] = useState<AskTurn[]>([]);
  const [pending, setPending] = useState<PendingAsk>();
  const controller = useRef<AbortController>();
  const nextId = useRef(1);
  const scopeRequested = useRef(false);

  const loadScope = useCallback(() => {
    if (scopeRequested.current) return;
    scopeRequested.current = true;
    setScope({ status: 'loading' });
    api.getScope().then(
      ({ repos }) =>
        setScope(
          repos.length > 0 ? { status: 'member', repos } : { status: 'search' },
        ),
      // Not configured, feature off (404), no session, or any other error:
      // everyone can still search.
      () => setScope({ status: 'search' }),
    );
  }, [api]);

  const ask = useCallback(
    (question: string) => {
      if (controller.current) return;
      const priorContext = buildPriorContext(
        turns.flatMap(turn =>
          turn.answer
            ? [{ question: turn.question, answer: turn.answer.answer }]
            : [],
        ),
      );
      const abort = new AbortController();
      controller.current = abort;
      setPending({ question, startedAt: Date.now() });

      const id = nextId.current++;
      api
        .ask(
          { question, ...(priorContext ? { priorContext } : {}) },
          { signal: abort.signal },
        )
        .then(
          answer =>
            setTurns(previous => [...previous, { id, question, answer }]),
          error =>
            setTurns(previous => [
              ...previous,
              { id, question, failure: toFailure(error) },
            ]),
        )
        .finally(() => {
          controller.current = undefined;
          setPending(undefined);
        });
    },
    [api, turns],
  );

  const stopWaiting = useCallback(() => controller.current?.abort(), []);

  const newConversation = useCallback(() => {
    if (!controller.current) setTurns([]);
  }, []);

  return {
    scope,
    loadScope,
    turns,
    pending,
    ask,
    stopWaiting,
    newConversation,
  };
}
